import _map from 'lodash/map';
import _find from 'lodash/find';
import _groupBy from 'lodash/groupBy';
import _maxBy from 'lodash/maxBy';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import Analytics from '@web-solutions/module-analytics';
import { T, t } from '@web-solutions/module-localization';

import { setTrialPrice } from '@web-solutions/core/store/billing/actions';
import { selectProducts, selectTrialPrice } from '@web-solutions/core/store/billing/selectors';
import { useRemoteConfig } from '@web-solutions/core/hooks/use-remote-config';

import { Title, Button, Modal } from '@web-solutions/core/ui-elements';
import { EVENT_ACTION } from '@web-solutions/core/constants/general';

import classes from './style.module.scss';

const tKey = 'core.trial_price_selection';
const eventCategory = 'trial_price_selection';

interface TrialPriceProps {
  ready: boolean,
}

export const TrialPrice: React.FC<TrialPriceProps> = ({ ready }) => {
  const products = useSelector(selectProducts);
  const trialPrice = useSelector(selectTrialPrice);
  const { isTrialPriceSelection } = useRemoteConfig();

  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    if (isTrialPriceSelection && !!products?.length && !trialPrice && ready) {
      const p = setTimeout(() => {
        setOpen(true);
      }, 1000);

      return () => {
        clearTimeout(p);
      }
    } else {
      setOpen(false);
    }
  }, [isTrialPriceSelection, products?.length, ready, trialPrice]);

  if (!products?.length) {
    return null;
  }

  return (
    <TrialPriceModal
      isOpen={isOpen}
    />
  );
};

interface TrialPriceModalProps {
  isOpen: boolean,
}

export const TrialPriceModal: React.FC<TrialPriceModalProps> = ({ isOpen }) => {
  const dispatch = useDispatch();

  const products = useSelector(selectProducts);

  // in state in order to avoid products filtering between submit and close
  const [options] = useState(_map(_groupBy(products, 'trialPriceAmount'), (v, k) => ({ label: v[0]?.trialPrice, value: +k })));

  const defaultProduct = useMemo(() => _find(products, 'default') || products[0], [products]);

  const [activeOption, setActiveOption] = useState(+defaultProduct?.trialPriceAmount);

  const max = _maxBy(options, t => t?.value);

  useEffect(() => {
    if (isOpen) {
      Analytics.trackEvent(eventCategory, EVENT_ACTION.OPEN);
    }
  }, [isOpen]);

  const handleClickTrial = (trial: number) => {
    setActiveOption(trial);
  };

  const handleSubmit = useCallback(() => {
    Analytics.trackEvent(eventCategory, EVENT_ACTION.SUCCESS, { answer: activeOption });
    dispatch(setTrialPrice(String(activeOption)))
  }, [activeOption, dispatch]);

  return (
    <Modal
      isOpen={isOpen}
      centered
      className={classes.modal}
    >
      <div>
        <Title level="h2" className={classes.title}>{t(`${tKey}.title`, { days: defaultProduct?.trialDays })}</Title>
        <T k={`${tKey}.subtitle`} tOptions={{ price: max?.label }} />
        <div className={classes.invoke}>
          <T k={`${tKey}.invoke`} tOptions={{ days: defaultProduct?.trialDays }} />
        </div>
        <div className={classes.options}>
          {options.map((option) => (
            <button
              key={option.label}
              className={classNames(classes.option, { [classes.active]: option.value === activeOption })}
              onClick={() => handleClickTrial(option.value)}>
              {option.label}
              {option.value === max?.value && (
                <>
                  <br />
                  <span className={classes.popular}>
                    <T k={`${tKey}.most_popular`} />
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
        <Button
          title={t(`${tKey}.button`)}
          className={classes.card__button}
          onClick={handleSubmit}
        />
      </div>
    </Modal>
  );
};
